import { Component, Vue, Watch } from 'vue-property-decorator';
import confetti from 'canvas-confetti';
import { CONFETTI_COLORS, CONTRIBUTION_LEVELS, LOCAL_STORAGE_KEYS, PAGE_NAMES } from '@/constants';
import Avatar from '@/components/common/avatar/Avatar.vue';
import NewLevelDialog from '@/components/common/on-boarding/new-level-dialog/NewLevelDialog.vue';
import ProductTutorialDialog from '@/components/common/on-boarding/product-tutorial-dialog/ProductTutorialDialog.vue';
import Tooltip from '@/components/common/tooltip/Tooltip.vue';
import { ContributionLevel } from '@/models';
import {fetchMyLevel, hasToken} from '@/services';
import { getContributionLevelFromDrops } from '@/helpers';

const SLIDE_MAX_IDX = 3;

@Component({
  components: {
    Avatar,
    NewLevelDialog,
    ProductTutorialDialog,
    Tooltip
  }
})
export default class OnBoarding extends Vue {
  private onBoardingSlideIdx = 0;
  private contributionLevel: ContributionLevel = {} as ContributionLevel;

  get productTutorialDialogShown(): boolean {
    return this.$store.getters['app/productTutorialDialogShown'];
  }

  get newLevelDialogShown(): boolean {
    return this.$store.getters['app/newLevelDialogShown'];
  }

  @Watch('productTutorialDialogShown')
  onDialogShownChanged(): void {
    if (this.productTutorialDialogShown) {
      this.onBoardingSlideIdx = 0;
    }
  }

  mounted(): void {
    if(hasToken()) {
      this.verifyProductTutorialVisibility();
    }
  }

  verifyProductTutorialVisibility(): void {
    const VIEWED = 'viewed';
    if (window.localStorage.getItem(LOCAL_STORAGE_KEYS.ON_BOARDING_VIEWED) !== VIEWED) {
      this.$store.commit('app/setProductTutorialDialogShown', true);
    } else {
      this.verifyNewLevelDialogVisibility();
    }
    window.localStorage.setItem(LOCAL_STORAGE_KEYS.ON_BOARDING_VIEWED, VIEWED);
  }

  async verifyNewLevelDialogVisibility(): Promise<void> {
    const lastAccessLevel = window.localStorage.getItem(LOCAL_STORAGE_KEYS.LAST_ACCESS_LEVEL);
    const userLevel = this.$store.getters['app/userLevel']?.totalEarnedDrops
      ? this.$store.getters['app/userLevel']
      : (await fetchMyLevel()).data;
    const contributionLevel = getContributionLevelFromDrops(userLevel.totalEarnedDrops);
    this.contributionLevel = contributionLevel;
    const currentLevel = `${contributionLevel.name}`;

    if (currentLevel !== CONTRIBUTION_LEVELS.EGG.name && lastAccessLevel !== currentLevel) {
      this.$store.commit('app/setNewLevelDialogShown', true);
      this.showConfetti();
      window.localStorage.setItem(LOCAL_STORAGE_KEYS.LAST_ACCESS_LEVEL, currentLevel);
    }
  }

  closeProductTutorialDialog(): void {
    this.$store.commit('app/setProductTutorialDialogShown', false);
    this.verifyNewLevelDialogVisibility();
  }

  closeNewLevelDialog(): void {
    this.$store.commit('app/setNewLevelDialogShown', false);
  }

  nextSlide(): void {
    if (this.onBoardingSlideIdx === SLIDE_MAX_IDX) {
      this.closeProductTutorialDialog();
    } else {
      this.onBoardingSlideIdx += 1;
    }
  }

  backSlide(): void {
    this.onBoardingSlideIdx = this.onBoardingSlideIdx === 0 ? SLIDE_MAX_IDX : this.onBoardingSlideIdx - 1;
  }

  navigateToWelcomePage(): void {
    this.$store.commit('app/setProductTutorialDialogShown', false);
    this.$router.push({ name: PAGE_NAMES.WELCOME });
  }

  showConfetti(): void {
    confetti({
      particleCount: 2,
      angle: 60,
      spread: 55,
      origin: { x: 0 },
      colors: CONFETTI_COLORS,
      zIndex: 99999
    });
    confetti({
      particleCount: 2,
      angle: 120,
      spread: 55,
      origin: { x: 1 },
      colors: CONFETTI_COLORS
    });
    if (this.newLevelDialogShown) {
      requestAnimationFrame(this.showConfetti);
    }
  }
}
